import { useState, useEffect, useCallback } from "react";
import {
  collection, addDoc, deleteDoc, updateDoc,
  doc, query, orderBy, onSnapshot, serverTimestamp, setDoc, getDoc
} from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";

const MONTHS = ["Enero","Febrero","Marzo","Abril","Mayo","Junio",
                "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

export function useRecurring() {
  const { user, profile } = useAuth();
  const [recurrents, setRecurrents] = useState([]);
  const [loading, setLoading] = useState(true);

  // BUGFIX: este hook seguía usando la ruta antigua users/{uid}/... sin
  // pasar por workspaceId, a diferencia de useEntries.js/useHuchas.js/
  // usePendientes.js, que ya migraron a este mismo patrón. Como TODAS las
  // cuentas tienen workspaceId asignado desde hace tiempo, y useEntries.js
  // ya solo lee de workspaces/{wsId}/entries, cualquier entrada recurrente
  // procesada automáticamente caía en una ruta que la app ya no mostraba
  // en ningún sitio — desaparecía en silencio. Antes de desplegar este
  // cambio, se ejecutó migrate-recurrents-to-workspace.js para copiar
  // (sin borrar) los datos existentes a la ruta nueva.
  const workspaceId = profile?.activeWorkspaceId || profile?.personalWorkspaceId || null;
  const recurrentsRef = useCallback(() => {
    if (workspaceId) return collection(db, "workspaces", workspaceId, "recurrents");
    return collection(db, "users", user.uid, "recurrents");
  }, [workspaceId, user]);
  const recurrentDoc = useCallback((id) => {
    if (workspaceId) return doc(db, "workspaces", workspaceId, "recurrents", id);
    return doc(db, "users", user.uid, "recurrents", id);
  }, [workspaceId, user]);
  const flagDoc = useCallback((key) => {
    if (workspaceId) return doc(db, "workspaces", workspaceId, "recurringFlags", key);
    return doc(db, "users", user.uid, "recurringFlags", key);
  }, [workspaceId, user]);
  const entriesRef = useCallback(() => {
    if (workspaceId) return collection(db, "workspaces", workspaceId, "entries");
    return collection(db, "users", user.uid, "entries");
  }, [workspaceId, user]);

  useEffect(() => {
    // Igual que en useHuchas.js: se espera a que workspaceId esté
    // disponible (no solo "user") antes de suscribirse, para no caer en
    // un permission-denied puntual en la breve ventana en que el perfil
    // aún no ha terminado de cargar.
    if (!user || !workspaceId) {
      setLoading(!!user);
      return;
    }
    const ref = recurrentsRef();
    const q   = query(ref, orderBy("createdAt", "desc"));
    const unsub = onSnapshot(q, snap => {
      setRecurrents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, [user, workspaceId]);

  async function addRecurrent(data) {
    if (!user) return;
    await addDoc(recurrentsRef(), {
      ...data,
      active: true,
      createdAt: serverTimestamp(),
    });
  }

  async function deleteRecurrent(id) {
    await deleteDoc(recurrentDoc(id));
  }

  async function toggleRecurrent(id, active) {
    await updateDoc(recurrentDoc(id), { active });
  }

  async function processRecurrents() {
    if (!user || !workspaceId || recurrents.length === 0) return;
    const now   = new Date();
    const year  = now.getFullYear();
    const month = MONTHS[now.getMonth()];
    const key   = `${year}-${month}`;

    const flagRef  = flagDoc(key);
    const flagSnap = await getDoc(flagRef);
    if (flagSnap.exists()) return;

    const active = recurrents.filter(r => r.active !== false);
    for (const r of active) {
      const day = r.day || 1;
      const mm  = String(now.getMonth()+1).padStart(2,"0");
      const dd  = String(day).padStart(2,"0");
      const dt  = `${dd}/${mm}/${year} 00:00`;
      // Si el recurrente tiene campos personalizados (modo negocio), se
      // trasladan también a la entrada generada automáticamente.
      const customData = {};
      ["custom1", "custom2", "custom3"].forEach(key => {
        if (r[key]) customData[key] = r[key];
      });
      await addDoc(entriesRef(), {
        type:        r.type,
        cat:         r.cat,
        desc:        r.desc,
        payment:     r.payment || "",
        amount:      r.amount,
        month,
        year,
        datetime:    dt,
        createdAt:   serverTimestamp(),
        isRecurring: true,
        ...customData,
      });
    }

    await setDoc(flagRef, { processedAt: serverTimestamp() });
  }

  return {
    recurrents, loading,
    addRecurrent, deleteRecurrent, toggleRecurrent, processRecurrents,
  };
}
